import type { ReactNode } from "react";
import { useDeferredValue } from "react";

import { Tabs } from "@similarweb/ui-components";

import type { BaseTab, PageContentTabBase } from "../../types";

import { StyledTab, StyledTabList, StyledTabPanel, StyledTabsContainer } from "./styles";

interface IProps<Tab> {
    tabs: readonly Tab[];
    selectedTabId: number;
    onTabSelect?: (tabId: number) => void;
    /** Rendered full width between the tab bar and the tab panels. */
    belowTabsSlot?: ReactNode;
}

const TabsContent = <Id extends string, Tab extends PageContentTabBase<BaseTab<Id>>>({
    tabs,
    selectedTabId,
    onTabSelect,
    belowTabsSlot,
}: IProps<Tab>) => {
    /*
     The panel components can have expensive renders (i.e. tables)
     -> tell React to wait until after the urgent updates have painted by using a deffered state value
     */
    const deferredSelectedIndex = useDeferredValue(selectedTabId);

    return (
        <StyledTabsContainer data-automation="page-content.tabs-wrapper">
            <Tabs
                className="page-content-tabs"
                selectedIndex={selectedTabId}
                onSelect={onTabSelect}
            >
                <StyledTabList>
                    {tabs.map((tab, index) => {
                        const shouldRenderDeferredContent = deferredSelectedIndex === index;
                        const TabComponent = tab.tabComponent;

                        return (
                            <StyledTab
                                key={tab.id}
                                data-automation={`${tab.id}-tab`}
                            >
                                <TabComponent
                                    isSelected={selectedTabId === index}
                                    shouldRenderDeferredContent={shouldRenderDeferredContent}
                                    tab={tab}
                                />
                            </StyledTab>
                        );
                    })}
                </StyledTabList>

                {belowTabsSlot}

                {tabs.map((tab, index) => {
                    const shouldRenderDeferredContent = deferredSelectedIndex === index;
                    const PanelComponent = tab.panelComponent;

                    return (
                        <StyledTabPanel
                            key={tab.id}
                            data-automation={`${tab.id}-tab-panel`}
                        >
                            <PanelComponent
                                isSelected={selectedTabId === index}
                                shouldRenderDeferredContent={shouldRenderDeferredContent}
                                tab={tab}
                            />
                        </StyledTabPanel>
                    );
                })}
            </Tabs>
        </StyledTabsContainer>
    );
};

export default TabsContent;
